import { processingApi } from "@/services/processingApi";
import { store } from "@/app/store";
import {
  applyProcessingLifecycleCountDelta,
  isEmptyArchivedDeleteTarget,
  isHistoryCountArgs,
  isStaleProcessingCountVersusExpected,
  listContainsExpectedJobIds,
  listOmitsExpectedJobIds,
  processingCountArgsBucket,
  processingLifecycleCountDelta,
  processingReconcileMaxAttempts,
  sourceClearedForLifecycleMove,
  PROCESSING_RECONCILE_DELAY_MS,
  PROCESSING_RECONCILE_RETRY_MS,
  type ProcessingLifecycleCountOp,
  type ProcessingLifecycleCountSnapshot,
} from "@/features/processing/lifecycleCountPatch";
import type {
  ApiEnvelope,
  ApprovedArtifactVersion,
  BulkLifecycleResponse,
  BulkReprocessRequest,
  BulkReprocessResponse,
  CorrectJobMetadataFields,
  CorrectJobMetadataResponse,
  CountProcessingJobsArgs,
  CountProcessingJobsResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  DuplicateAction,
  DuplicateOutcome,
  DuplicateTrackMatch,
  DuplicateUploadDetails,
  DuplicateUploadReasonCode,
  LifecycleActionResult,
  ReplaceSongAudioAction,
  ReplaceSongAudioRequest,
  ReplaceSongAudioResponse,
  ListApprovedArtifactVersionsArgs,
  ListApprovedArtifactVersionsResponse,
  ListProcessingJobsArgs,
  ListProcessingJobsResponse,
  CreateProcessingBulkOpRequest,
  CreateProcessingBulkOpResponse,
  GetProcessingBulkOpResponse,
  ApplyProcessingBulkOpRequest,
  ProcessingJob,
  ProcessingJobRow,
  ReprocessJobResponse,
  ResumeJobRequest,
  ResumeJobResponse,
  RevokeArtifactResponse,
} from "./processingTypes";

/** Default first-page list args used by Song Processing tabs (PAGE_SIZE=100). */
const DEFAULT_ACTIVE_LIST_ARGS: ListProcessingJobsArgs = { limit: 100 };
const DEFAULT_ARCHIVED_LIST_ARGS: ListProcessingJobsArgs = {
  archived: true,
  limit: 100,
};

function isArchivedListArgs(
  args: ListProcessingJobsArgs | void | null | undefined,
): boolean {
  return !!(args && args.archived === true);
}

// Slice #6 — Processing Dashboard + P2 upload ingress (POST /uploads) + U1a
// lifecycle mutations (reprocess / resume / revoke, P5b endpoints).
// Injected onto the separate processingApi client (music-processing base URL).
// Envelope/unwrap + providesTags mirror recordLabelsApi.ts.
//
// PJL lifecycle: optimistic Active/Archived count + list-membership patches +
// generation-guarded delayed LIST reconcile (same class of fix as Song Library).
// Mutations must NOT immediately invalidate LIST — projection lag can overwrite
// tab totals and omit confirmed jobIds from the destination list.

export const processingJobsApi = processingApi.injectEndpoints({
  endpoints: (builder) => ({
    listProcessingJobs: builder.query<
      ListProcessingJobsResponse,
      ListProcessingJobsArgs | void
    >({
      query: (args) => {
        const params: Record<string, string | number | boolean> = {};
        if (args) {
          if (args.state) params.state = args.state;
          if (args.decision) params.decision = args.decision;
          if (args.failedCheckpoint) params.failedCheckpoint = args.failedCheckpoint;
          if (args.needsReview) params.needsReview = true;
          if (args.uploaderSub) params.uploaderSub = args.uploaderSub;
          if (args.lineageTrackId) params.lineageTrackId = args.lineageTrackId;
          if (args.fromDate) params.fromDate = args.fromDate;
          if (args.toDate) params.toDate = args.toDate;
          // Song Uploader intake queue — Active-only post-filtered scope.
          if (args.intakeQueue) params.intakeQueue = true;
          // PJL-3 — Archived view. Only sent when true; the API default
          // (Active view) stays untouched for all pre-PJL callers.
          if (args.archived) params.archived = true;
          // Governance — History partition (superseded governed jobs).
          if (args.history) params.history = true;
          // PJL-3.1 — free-text search within the current view.
          if (args.search) params.search = args.search;
          if (args.limit) params.limit = args.limit;
          if (args.page) params.page = args.page;
          if (args.pageSize) params.pageSize = args.pageSize;
          if (args.pageToken) params.pageToken = args.pageToken;
        }
        return {
          url: "/processing-jobs",
          method: "GET",
          params,
        };
      },
      transformResponse: (raw: ApiEnvelope<ListProcessingJobsResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load Song Processing jobs",
          );
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.jobs
          ? [
              ...result.jobs.map((row) => ({
                type: "ProcessingJobs" as const,
                id: row.jobId,
              })),
              { type: "ProcessingJobs" as const, id: "LIST" },
            ]
          : [{ type: "ProcessingJobs" as const, id: "LIST" }],
    }),
    // PJL-3.2 — server-side totals for tab labels ("Active (438)"),
    // "Showing X of Y matching jobs", and total pages. Same scope semantics
    // as listProcessingJobs; provides the LIST tag so every lifecycle
    // mutation automatically refreshes the counts too.
    countProcessingJobs: builder.query<
      CountProcessingJobsResponse,
      CountProcessingJobsArgs | void
    >({
      query: (args) => {
        const params: Record<string, string | number | boolean> = { count: true };
        if (args) {
          if (args.state) params.state = args.state;
          if (args.decision) params.decision = args.decision;
          if (args.failedCheckpoint) params.failedCheckpoint = args.failedCheckpoint;
          if (args.needsReview) params.needsReview = true;
          if (args.uploaderSub) params.uploaderSub = args.uploaderSub;
          if (args.lineageTrackId) params.lineageTrackId = args.lineageTrackId;
          if (args.fromDate) params.fromDate = args.fromDate;
          if (args.toDate) params.toDate = args.toDate;
          if (args.intakeQueue) params.intakeQueue = true;
          if (args.archived) params.archived = true;
          if (args.history) params.history = true;
          if (args.search) params.search = args.search;
        }
        return {
          url: "/processing-jobs",
          method: "GET",
          params,
        };
      },
      transformResponse: (raw: ApiEnvelope<CountProcessingJobsResponse>) => {
        // Deploy-order safety: an API that doesn't understand ?count=true yet
        // answers with a normal list payload (no `count`). Degrade to the
        // sentinel count:-1 ("counts unavailable" — the UI hides the labels)
        // rather than throwing, which would spam console errors.
        if (
          !raw ||
          raw.success !== true ||
          !raw.data ||
          typeof (raw.data as { count?: unknown }).count !== "number"
        ) {
          return { count: -1, exact: true, archived: false, history: false };
        }
        return raw.data;
      },
      providesTags: [{ type: "ProcessingJobs" as const, id: "LIST" }],
    }),
    getProcessingJob: builder.query<ProcessingJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<{ job: ProcessingJob }>) => {
        if (!raw || raw.success !== true || !raw.data || !raw.data.job) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load Song Processing job",
          );
        }
        return raw.data.job;
      },
      providesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
      ],
    }),
    listApprovedArtifactVersions: builder.query<
      ListApprovedArtifactVersionsResponse,
      ListApprovedArtifactVersionsArgs
    >({
      query: (args) => {
        const params: Record<string, string> = {};
        if (args.trackId) params.trackId = args.trackId;
        if (args.sourceProcessingJobId)
          params.sourceProcessingJobId = args.sourceProcessingJobId;
        if (args.artifactVersionId)
          params.artifactVersionId = args.artifactVersionId;
        return {
          url: "/approved-artifact-versions",
          method: "GET",
          params,
        };
      },
      transformResponse: (
        raw: ApiEnvelope<ListApprovedArtifactVersionsResponse>,
      ) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load approved artifact versions",
          );
        }
        return raw.data;
      },
      providesTags: (result) =>
        result && result.artifactVersions
          ? [
              ...result.artifactVersions.map((row) => ({
                type: "ApprovedArtifactVersions" as const,
                id: row.artifactVersionId,
              })),
              { type: "ApprovedArtifactVersions" as const, id: "LIST" },
            ]
          : [{ type: "ApprovedArtifactVersions" as const, id: "LIST" }],
    }),
    getApprovedArtifactVersion: builder.query<
      ApprovedArtifactVersion,
      { artifactVersionId: string }
    >({
      query: ({ artifactVersionId }) => ({
        url: `/approved-artifact-versions/${encodeURIComponent(artifactVersionId)}`,
        method: "GET",
      }),
      transformResponse: (
        raw: ApiEnvelope<{ artifactVersion: ApprovedArtifactVersion }>,
      ) => {
        if (
          !raw ||
          raw.success !== true ||
          !raw.data ||
          !raw.data.artifactVersion
        ) {
          throw new Error(
            raw && raw.message
              ? raw.message
              : "Failed to load approved artifact version",
          );
        }
        return raw.data.artifactVersion;
      },
      providesTags: (_result, _error, { artifactVersionId }) => [
        { type: "ApprovedArtifactVersions" as const, id: artifactVersionId },
      ],
    }),
    createUpload: builder.mutation<CreateUploadResponse, CreateUploadRequest>({
      query: (body) => ({
        url: "/uploads",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<CreateUploadResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to start upload",
          );
        }
        return raw.data;
      },
      invalidatesTags: [{ type: "ProcessingJobs", id: "LIST" }],
    }),
    replaceSongAudio: builder.mutation<
      ReplaceSongAudioResponse,
      { trackId: string } & ReplaceSongAudioRequest
    >({
      query: ({ trackId, ...body }) => ({
        url: `/tracks/${encodeURIComponent(trackId)}/replace-audio`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<ReplaceSongAudioResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to start Replace Song Audio",
          );
        }
        return raw.data;
      },
      invalidatesTags: [{ type: "ProcessingJobs", id: "LIST" }],
    }),
    reprocessProcessingJob: builder.mutation<
      ReprocessJobResponse,
      { jobId: string }
    >({
      query: ({ jobId }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/reprocess`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<ReprocessJobResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to reprocess job",
          );
        }
        return raw.data;
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
        { type: "ProcessingJobs" as const, id: "LIST" },
      ],
    }),
    bulkReprocessProcessingJobs: builder.mutation<
      BulkReprocessResponse,
      BulkReprocessRequest
    >({
      query: (body) => ({
        url: "/processing-jobs/reprocess",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<BulkReprocessResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to reprocess jobs",
          );
        }
        return raw.data;
      },
      invalidatesTags: [{ type: "ProcessingJobs", id: "LIST" }],
    }),
    resumeProcessingJob: builder.mutation<
      ResumeJobResponse,
      { jobId: string; corrections?: ResumeJobRequest }
    >({
      query: ({ jobId, corrections }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/resume`,
        method: "POST",
        body: corrections && Object.keys(corrections).length > 0 ? corrections : {},
      }),
      transformResponse: (raw: ApiEnvelope<ResumeJobResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to resume job",
          );
        }
        return raw.data;
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
        { type: "ProcessingJobs" as const, id: "LIST" },
      ],
    }),
    // MDC-1 — post-approval metadata correction (Decision Audit "Edit
    // Metadata"). Corrects the canonical Track (catalog relink included) and
    // appends an immutable correction record to the job's Decision Audit.
    correctJobMetadata: builder.mutation<
      CorrectJobMetadataResponse,
      { jobId: string; reason: string; fields: CorrectJobMetadataFields }
    >({
      query: ({ jobId, reason, fields }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/correct-metadata`,
        method: "POST",
        body: { reason, fields },
      }),
      transformResponse: (raw: ApiEnvelope<CorrectJobMetadataResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to correct metadata",
          );
        }
        return raw.data;
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
        { type: "ProcessingJobs" as const, id: "LIST" },
      ],
    }),
    // ---- PJL-3 lifecycle management (archive / restore / hard delete) --------
    archiveProcessingJob: builder.mutation<
      LifecycleActionResult,
      { jobId: string; reason?: string }
    >({
      query: ({ jobId, reason }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/archive`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      transformResponse: (raw: ApiEnvelope<LifecycleActionResult>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to archive job");
        }
        return raw.data;
      },
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchProcessingJobsCounts(dispatch, "archive", 1);
          patchProcessingJobsLists(dispatch, [jobId], "archive");
          scheduleProcessingJobsListReconcile(dispatch, {
            op: "archive",
            expectedJobIds: [jobId],
          });
        } catch {
          // Mutation failed — leave caches unchanged.
        }
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
        // No LIST — projection race overwrites optimistic Active/Archived counts.
      ],
    }),
    restoreProcessingJob: builder.mutation<LifecycleActionResult, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<LifecycleActionResult>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to restore job");
        }
        return raw.data;
      },
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchProcessingJobsCounts(dispatch, "restore", 1);
          patchProcessingJobsLists(dispatch, [jobId], "restore");
          scheduleProcessingJobsListReconcile(dispatch, {
            op: "restore",
            expectedJobIds: [jobId],
          });
        } catch {
          // Mutation failed — leave caches unchanged.
        }
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "ProcessingJobs" as const, id: jobId },
      ],
    }),
    bulkArchiveProcessingJobs: builder.mutation<
      BulkLifecycleResponse,
      { ids: string[]; reason?: string }
    >({
      query: ({ ids, reason }) => ({
        url: "/processing-jobs/archive",
        method: "POST",
        body: reason ? { ids, reason } : { ids },
      }),
      transformResponse: (raw: ApiEnvelope<BulkLifecycleResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to archive jobs");
        }
        return raw.data;
      },
      // No invalidatesTags — page patches counts + schedules generation-guarded
      // reconcile after confirmed okIds (see processing-jobs.tsx).
    }),
    createProcessingBulkOp: builder.mutation<
      CreateProcessingBulkOpResponse,
      CreateProcessingBulkOpRequest
    >({
      query: (body) => ({
        url: "/processing-jobs/bulk-operations",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<CreateProcessingBulkOpResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to create bulk operation",
          );
        }
        return raw.data;
      },
    }),
    getProcessingBulkOp: builder.query<
      GetProcessingBulkOpResponse,
      { bulkOpId: string }
    >({
      query: ({ bulkOpId }) => ({
        url: `/processing-jobs/bulk-operations/${encodeURIComponent(bulkOpId)}`,
      }),
      transformResponse: (raw: ApiEnvelope<GetProcessingBulkOpResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to load bulk operation",
          );
        }
        return raw.data;
      },
    }),
    cancelProcessingBulkOp: builder.mutation<
      GetProcessingBulkOpResponse,
      { bulkOpId: string }
    >({
      query: ({ bulkOpId }) => ({
        url: `/processing-jobs/bulk-operations/${encodeURIComponent(bulkOpId)}/cancel`,
        method: "POST",
        body: {},
      }),
      transformResponse: (raw: ApiEnvelope<GetProcessingBulkOpResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to cancel bulk operation",
          );
        }
        return raw.data;
      },
    }),
    applyProcessingBulkOp: builder.mutation<
      GetProcessingBulkOpResponse,
      ApplyProcessingBulkOpRequest
    >({
      query: ({ bulkOpId, ...body }) => ({
        url: `/processing-jobs/bulk-operations/${encodeURIComponent(bulkOpId)}/apply`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<GetProcessingBulkOpResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to apply bulk delete",
          );
        }
        return raw.data;
      },
    }),
    bulkRestoreProcessingJobs: builder.mutation<
      BulkLifecycleResponse,
      { ids: string[] }
    >({
      query: ({ ids }) => ({
        url: "/processing-jobs/restore",
        method: "POST",
        body: { ids },
      }),
      transformResponse: (raw: ApiEnvelope<BulkLifecycleResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to restore jobs");
        }
        return raw.data;
      },
      // No invalidatesTags — see bulkArchiveProcessingJobs.
    }),
    // PJL-2 — hard delete (Super Admin only; archived, non-lineage jobs only).
    // The typed confirmation is enforced server-side too, so the confirm token
    // is part of the request body by contract.
    deleteProcessingJob: builder.mutation<
      LifecycleActionResult,
      { jobId: string; reason?: string }
    >({
      query: ({ jobId, reason }) => ({
        url: `/processing-jobs/${encodeURIComponent(jobId)}/delete`,
        method: "POST",
        body: reason ? { confirm: "DELETE", reason } : { confirm: "DELETE" },
      }),
      transformResponse: (raw: ApiEnvelope<LifecycleActionResult>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to delete job");
        }
        return raw.data;
      },
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          patchProcessingJobsCounts(dispatch, "delete", 1);
          patchProcessingJobsLists(dispatch, [jobId], "delete");
          scheduleProcessingJobsListReconcile(dispatch, {
            op: "delete",
            expectedJobIds: [jobId],
          });
        } catch {
          // Mutation failed — leave caches unchanged.
        }
      },
      // No LIST invalidation — same projection race as archive/restore.
      invalidatesTags: () => [],
    }),
    bulkDeleteProcessingJobs: builder.mutation<
      BulkLifecycleResponse,
      { ids: string[]; reason: string; operationId?: string }
    >({
      query: ({ ids, reason, operationId }) => ({
        url: "/processing-jobs/delete",
        method: "POST",
        body: {
          ids,
          confirm: "DELETE",
          reason,
          ...(operationId ? { operationId } : {}),
        },
      }),
      transformResponse: (raw: ApiEnvelope<BulkLifecycleResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(raw && raw.message ? raw.message : "Failed to delete jobs");
        }
        return raw.data;
      },
      // No invalidatesTags — see bulkArchiveProcessingJobs.
    }),
    revokeApprovedArtifactVersion: builder.mutation<
      RevokeArtifactResponse,
      { artifactVersionId: string; reason?: string }
    >({
      query: ({ artifactVersionId, reason }) => ({
        url: `/approved-artifact-versions/${encodeURIComponent(artifactVersionId)}/revoke`,
        method: "POST",
        body: reason ? { reason } : {},
      }),
      transformResponse: (raw: ApiEnvelope<RevokeArtifactResponse>) => {
        if (!raw || raw.success !== true || !raw.data) {
          throw new Error(
            raw && raw.message ? raw.message : "Failed to revoke artifact version",
          );
        }
        return raw.data;
      },
      invalidatesTags: (_result, _error, { artifactVersionId }) => [
        { type: "ApprovedArtifactVersions" as const, id: artifactVersionId },
        { type: "ApprovedArtifactVersions" as const, id: "LIST" },
        { type: "ProcessingJobs" as const, id: "LIST" },
      ],
    }),
  }),
});

function readProcessingCountSnapshot(): ProcessingLifecycleCountSnapshot {
  const state = store.getState();
  const cachedArgs = processingJobsApi.util.selectCachedArgsForQuery(
    state,
    "countProcessingJobs",
  );
  let active = 0;
  let archived = 0;
  let sawActive = false;
  let sawArchived = false;
  for (const args of cachedArgs) {
    if (isHistoryCountArgs(args)) continue;
    const entry = processingJobsApi.endpoints.countProcessingJobs.select(args)(state);
    const count = entry.data?.count;
    if (typeof count !== "number" || !Number.isFinite(count) || count < 0) continue;
    const bucket = processingCountArgsBucket(args);
    // Prefer unfiltered tab totals when present (exact Active / Archived labels).
    const isTabArgs =
      !args ||
      (Object.keys(args).length === 0) ||
      (Object.keys(args).length === 1 && args.archived === true);
    if (bucket === "active" && (isTabArgs || !sawActive)) {
      active = count;
      if (isTabArgs) sawActive = true;
    }
    if (bucket === "archived" && (isTabArgs || !sawArchived)) {
      archived = count;
      if (isTabArgs) sawArchived = true;
    }
  }
  return { active, archived };
}

/**
 * Optimistically patch every cached countProcessingJobs entry so Active /
 * Archived tab labels move immediately with the list (never list=2, tab=1).
 */
export function patchProcessingJobsCounts(
  dispatch: typeof store.dispatch,
  op: ProcessingLifecycleCountOp,
  times = 1,
) {
  const n = Math.max(1, Math.floor(times));
  const cachedArgs = processingJobsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "countProcessingJobs",
  );
  for (const args of cachedArgs) {
    if (isHistoryCountArgs(args)) continue;
    dispatch(
      processingJobsApi.util.updateQueryData("countProcessingJobs", args, (draft) => {
        if (!draft || typeof draft.count !== "number" || draft.count < 0) return;
        const bucket = processingCountArgsBucket(args);
        let next = draft.count;
        for (let i = 0; i < n; i += 1) {
          next = applyProcessingLifecycleCountDelta(
            next,
            processingLifecycleCountDelta(op, bucket),
          );
        }
        draft.count = next;
      }),
    );
  }
}

function collectCachedProcessingJobRows(
  jobIds: string[],
): Map<string, ProcessingJobRow> {
  const want = new Set(jobIds);
  const found = new Map<string, ProcessingJobRow>();
  const state = store.getState();
  const listArgs = processingJobsApi.util.selectCachedArgsForQuery(
    state,
    "listProcessingJobs",
  );
  for (const args of listArgs) {
    const entry = processingJobsApi.endpoints.listProcessingJobs.select(args)(
      state,
    );
    for (const job of entry.data?.jobs ?? []) {
      if (want.has(job.jobId) && !found.has(job.jobId)) {
        found.set(job.jobId, job);
      }
    }
  }
  for (const jobId of jobIds) {
    if (found.has(jobId)) continue;
    const detail = processingJobsApi.endpoints.getProcessingJob.select({
      jobId,
    })(state);
    if (detail.data) {
      found.set(jobId, detail.data);
    }
  }
  return found;
}

function minimalJobRowStub(jobId: string, archived: boolean): ProcessingJobRow {
  const now = new Date().toISOString();
  return {
    jobId,
    songType: "commercial",
    state: "approved",
    uploadSource: "",
    actorEmployeeId: "",
    actorMasterAccountId: "",
    originalFilename: "",
    discoveredTitle: null,
    discoveredArtist: null,
    reasonCode: "",
    currentCheckpoint: "",
    checkpointSummary: {
      total: 0,
      pass: 0,
      fail: 0,
      warning: 0,
      pending: 0,
      skipped: 0,
    },
    metadataConfidence: 0,
    malwareStatus: "",
    policyStatus: "",
    approvedArtifactVersionId: "",
    trackId: "",
    attempt: 0,
    reprocessOfJobId: "",
    correlationId: "",
    createdAt: now,
    updatedAt: now,
    version: 0,
    archived,
    archivedAt: archived ? now : null,
  };
}

/**
 * Empty cached list pages for a partition (used after a full-partition wipe
 * where individual deleted jobIds are not available client-side).
 */
export function emptyProcessingJobsListCaches(
  dispatch: typeof store.dispatch,
  bucket: "active" | "archived",
) {
  const cachedArgs = processingJobsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listProcessingJobs",
  );
  for (const args of cachedArgs) {
    if (isHistoryCountArgs(args)) continue;
    const archivedView = isArchivedListArgs(args);
    if (bucket === "archived" ? !archivedView : archivedView) continue;
    dispatch(
      processingJobsApi.util.updateQueryData(
        "listProcessingJobs",
        args,
        (draft) => {
          if (!draft || !Array.isArray(draft.jobs)) return;
          draft.jobs = [];
          draft.pageToken = null;
          if (typeof draft.totalCount === "number") draft.totalCount = 0;
        },
      ),
    );
  }
}

/**
 * Symmetric Active ↔ Archived list membership patches (Song Library pattern).
 * Archive removes from Active caches and upserts into Archived (including a
 * cold default Archived first page via upsertQueryData). Restore reverses.
 * Identity is always jobId — duplicate titles never collapse.
 */
export function patchProcessingJobsLists(
  dispatch: typeof store.dispatch,
  jobIds: string[],
  op: ProcessingLifecycleCountOp,
  knownRows?: ReadonlyMap<string, ProcessingJobRow> | null,
) {
  if (!jobIds.length) return;
  const cachedRows = collectCachedProcessingJobRows(jobIds);
  if (knownRows) {
    for (const [id, row] of knownRows) {
      if (!cachedRows.has(id)) cachedRows.set(id, row);
    }
  }
  const now = new Date().toISOString();
  const cachedArgs = processingJobsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listProcessingJobs",
  );

  const upsertIntoDraft = (
    draft: ListProcessingJobsResponse,
    jobId: string,
    archived: boolean,
  ) => {
    const existing = draft.jobs.find((j) => j.jobId === jobId);
    const src = cachedRows.get(jobId);
    if (existing) {
      existing.archived = archived;
      if (archived) {
        existing.archivedAt = existing.archivedAt || now;
      } else {
        existing.restoredAt = now;
      }
      return;
    }
    const base = src
      ? { ...src }
      : minimalJobRowStub(jobId, archived);
    base.archived = archived;
    base.archivedAt = archived ? now : base.archivedAt ?? null;
    if (!archived) base.restoredAt = now;
    draft.jobs = [base, ...draft.jobs];
  };

  for (const args of cachedArgs) {
    const archivedView = isArchivedListArgs(args);
    dispatch(
      processingJobsApi.util.updateQueryData(
        "listProcessingJobs",
        args,
        (draft) => {
          if (!draft || !Array.isArray(draft.jobs)) return;
          for (const jobId of jobIds) {
            if (op === "archive") {
              if (!archivedView) {
                draft.jobs = draft.jobs.filter((j) => j.jobId !== jobId);
              } else {
                upsertIntoDraft(draft, jobId, true);
              }
            } else if (op === "restore") {
              if (archivedView) {
                draft.jobs = draft.jobs.filter((j) => j.jobId !== jobId);
              } else {
                upsertIntoDraft(draft, jobId, false);
              }
            } else {
              // delete
              draft.jobs = draft.jobs.filter((j) => j.jobId !== jobId);
            }
          }
        },
      ),
    );
  }

  // Seed cold destination first-page cache so switching tabs never waits on
  // a lagging projection that can omit one of N confirmed jobIds.
  if (op === "archive" || op === "restore") {
    const destArchived = op === "archive";
    const destArgs = destArchived
      ? DEFAULT_ARCHIVED_LIST_ARGS
      : DEFAULT_ACTIVE_LIST_ARGS;
    const destEntry = processingJobsApi.endpoints.listProcessingJobs.select(
      destArgs,
    )(store.getState());
    if (!destEntry.data) {
      const seeded: ProcessingJobRow[] = jobIds.map((jobId) => {
        const src = cachedRows.get(jobId);
        const base = src ? { ...src } : minimalJobRowStub(jobId, destArchived);
        base.archived = destArchived;
        base.archivedAt = destArchived ? now : base.archivedAt ?? null;
        if (!destArchived) base.restoredAt = now;
        return base;
      });
      dispatch(
        processingJobsApi.util.upsertQueryData("listProcessingJobs", destArgs, {
          jobs: seeded,
          pageToken: null,
          limit: 100,
        }),
      );
    } else {
      // Dest cache may already exist from a lagging fetch with a partial set —
      // ensure every expected id is present.
      dispatch(
        processingJobsApi.util.updateQueryData(
          "listProcessingJobs",
          destArgs,
          (draft) => {
            if (!draft) return;
            for (const jobId of jobIds) {
              upsertIntoDraft(draft, jobId, destArchived);
            }
          },
        ),
      );
    }
  }
}

/**
 * After Song permanent delete / lineage wipe: governed Processing Job rows
 * must leave Song Uploader "Recent Song Processing" without a hard refresh.
 *
 * A single delayed LIST invalidate (TRACKS_RECONCILE_DELAY_MS) can refetch
 * while wipe projection is still lagging, then stick the deleted Song's
 * Approved job in RTK cache forever (polling is off when idle). Optimism
 * removes lineage matches immediately; an invalidate ladder retries until
 * the server Active list is empty for that lineage.
 */
let songDeleteProcessingInvalidateGeneration = 0;

export const SONG_DELETE_PROCESSING_INVALIDATE_DELAYS_MS = Object.freeze([
  0, 2500, 5000, 10000,
]);

/** Pure matcher used by optimistic Recent/Active list strip after Song wipe. */
export function jobBelongsToDeletedSongLineage(
  job: { trackId?: string | null; lineageTrackId?: string | null },
  deletedTrackIds: Iterable<string>,
): boolean {
  const idSet =
    deletedTrackIds instanceof Set
      ? deletedTrackIds
      : new Set(
          Array.from(deletedTrackIds).filter(
            (id) => typeof id === "string" && id.length > 0,
          ),
        );
  if (!idSet.size) return false;
  const trackId = job.trackId || null;
  const lineage = job.lineageTrackId || null;
  return (
    (!!trackId && idSet.has(trackId)) || (!!lineage && idSet.has(lineage))
  );
}

export function filterOutDeletedSongLineageJobs<
  T extends { trackId?: string | null; lineageTrackId?: string | null },
>(jobs: T[], deletedTrackIds: Iterable<string>): T[] {
  const idSet = new Set(
    Array.from(deletedTrackIds).filter(
      (id) => typeof id === "string" && id.length > 0,
    ),
  );
  if (!idSet.size) return jobs;
  return jobs.filter((j) => !jobBelongsToDeletedSongLineage(j, idSet));
}

export function removeLineageJobsFromProcessingLists(
  dispatch: typeof store.dispatch,
  lineageTrackIds: string[],
) {
  const ids = Array.from(
    new Set(
      (lineageTrackIds || []).filter(
        (id) => typeof id === "string" && id.length > 0,
      ),
    ),
  );
  if (!ids.length) return;
  const cachedArgs = processingJobsApi.util.selectCachedArgsForQuery(
    store.getState(),
    "listProcessingJobs",
  );
  for (const args of cachedArgs) {
    dispatch(
      processingJobsApi.util.updateQueryData(
        "listProcessingJobs",
        args,
        (draft) => {
          if (!draft || !Array.isArray(draft.jobs)) return;
          draft.jobs = filterOutDeletedSongLineageJobs(draft.jobs, ids);
        },
      ),
    );
  }
}

export function scheduleProcessingJobsInvalidateAfterSongDelete(
  dispatch: typeof store.dispatch,
  lineageTrackIds: string[],
) {
  removeLineageJobsFromProcessingLists(dispatch, lineageTrackIds);
  const generation = ++songDeleteProcessingInvalidateGeneration;
  for (const delayMs of SONG_DELETE_PROCESSING_INVALIDATE_DELAYS_MS) {
    window.setTimeout(() => {
      if (generation !== songDeleteProcessingInvalidateGeneration) return;
      // Re-apply optimism before each refetch so a lagging list cannot
      // resurrect wiped lineage rows between ladder steps.
      removeLineageJobsFromProcessingLists(dispatch, lineageTrackIds);
      dispatch(
        processingJobsApi.util.invalidateTags([
          { type: "ProcessingJobs", id: "LIST" },
        ]),
      );
    }, delayMs);
  }
}

/**
 * Lifecycle Mirror — Song Library Archive/Restore soft-mirrors governed
 * Processing jobs. Tracks mutations must refresh Song Processing Active +
 * Archived lists/counts (same LIST tag) without a manual browser refresh.
 *
 * Uses RTK invalidateTags (not polling). Immediate + delayed ladder covers
 * projection catch-up after coordinatedMirror, matching Song PD freshness.
 */
let songLifecycleMirrorProcessingInvalidateGeneration = 0;

/** Same ladder as Song PD — keep Archive/Restore freshness in lockstep. */
export const SONG_LIFECYCLE_MIRROR_PROCESSING_INVALIDATE_DELAYS_MS =
  SONG_DELETE_PROCESSING_INVALIDATE_DELAYS_MS;

/** Collect jobIds for a Track from any cached Song Processing list/detail. */
export function collectCachedProcessingJobIdsForTrack(trackId: string): string[] {
  if (!trackId) return [];
  const ids = new Set<string>();
  const state = store.getState();
  const listArgs = processingJobsApi.util.selectCachedArgsForQuery(
    state,
    "listProcessingJobs",
  );
  for (const args of listArgs) {
    const entry = processingJobsApi.endpoints.listProcessingJobs.select(args)(
      state,
    );
    for (const job of entry.data?.jobs ?? []) {
      if (
        job &&
        job.jobId &&
        (job.trackId === trackId || job.lineageTrackId === trackId)
      ) {
        ids.add(job.jobId);
      }
    }
  }
  return Array.from(ids);
}

export function scheduleProcessingJobsInvalidateAfterSongLifecycleMirror(
  dispatch: typeof store.dispatch,
  args: {
    trackId: string;
    op: "archive" | "restore";
    /** Optional known job ids (e.g. sourceProcessingJobId) when SP lists are cold. */
    hintJobIds?: string[];
  },
) {
  const trackId = typeof args.trackId === "string" ? args.trackId.trim() : "";
  if (!trackId) return;
  const op = args.op === "restore" ? "restore" : "archive";
  const hints = (args.hintJobIds || []).filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
  const jobIds = Array.from(
    new Set([...collectCachedProcessingJobIdsForTrack(trackId), ...hints]),
  );

  if (jobIds.length > 0) {
    patchProcessingJobsCounts(dispatch, op, jobIds.length);
    patchProcessingJobsLists(dispatch, jobIds, op);
  }

  const generation = ++songLifecycleMirrorProcessingInvalidateGeneration;
  for (const delayMs of SONG_LIFECYCLE_MIRROR_PROCESSING_INVALIDATE_DELAYS_MS) {
    window.setTimeout(() => {
      if (generation !== songLifecycleMirrorProcessingInvalidateGeneration) {
        return;
      }
      // Re-apply optimism before each refetch so lagging projection cannot
      // briefly resurrect the pre-mirror Active/Archived membership.
      if (jobIds.length > 0) {
        patchProcessingJobsLists(dispatch, jobIds, op);
      }
      dispatch(
        processingJobsApi.util.invalidateTags([
          { type: "ProcessingJobs", id: "LIST" },
        ]),
      );
    }, delayMs);
  }
}

/** Generation counter — ignore stale in-flight reconcile responses. */
let processingReconcileGeneration = 0;

export type ScheduleProcessingReconcileOptions = {
  delayMs?: number;
  op?: ProcessingLifecycleCountOp;
  /**
   * Confirmed jobIds that must appear on the destination list (archive →
   * Archived, restore → Active) before LIST invalidate is allowed. Prevents a
   * lagging 2-of-3 Archived page from replacing the optimistic 3-row cache.
   */
  expectedJobIds?: string[];
  /** Override scaled max attempts (tests / large bulk). */
  maxAttempts?: number;
  /** Called after counts converge and LIST is invalidated (or on give-up keep). */
  onSettled?: (result: "converged" | "kept-optimistic") => void;
  /** Visible operator signal when the projection never converges. */
  onReconcileFailed?: () => void;
};

/**
 * Delayed LIST reconcile after lifecycle success (projection catch-up).
 * Generation-guarded; rejects stale Active/Archived counts vs optimistic
 * expected snapshot; also rejects incomplete destination list membership;
 * retries until converge or max attempts. Never lets an older incomplete
 * list response stick as the source of truth.
 *
 * Delete is special: archived count still above expected is normal stream
 * lag after mass hard-delete. Keeping optimistic empty totals is success —
 * do not toast. Only toast when confirmed-deleted jobIds still appear on
 * Active/Archived list pages after retries.
 */
export function scheduleProcessingJobsListReconcile(
  dispatch: typeof store.dispatch,
  delayMsOrOpts:
    | number
    | ScheduleProcessingReconcileOptions = PROCESSING_RECONCILE_DELAY_MS,
) {
  const opts: ScheduleProcessingReconcileOptions =
    typeof delayMsOrOpts === "number"
      ? { delayMs: delayMsOrOpts }
      : delayMsOrOpts || {};
  const delayMs = opts.delayMs ?? PROCESSING_RECONCILE_DELAY_MS;
  const op = opts.op;
  const expectedJobIds = opts.expectedJobIds ?? [];
  const maxAttempts =
    opts.maxAttempts ?? processingReconcileMaxAttempts(expectedJobIds.length);

  const expected = readProcessingCountSnapshot();
  const generation = ++processingReconcileGeneration;

  // Full Archived wipe: clear list pages immediately so tab=0 and table=empty
  // agree while the projection count still lags (avoids false reconcile toast
  // and Active/Archived count-vs-row desync).
  if (op === "delete" && isEmptyArchivedDeleteTarget(expected)) {
    emptyProcessingJobsListCaches(dispatch, "archived");
  }

  const readCount = (res: unknown): number => {
    if (
      res &&
      typeof res === "object" &&
      "data" in res &&
      (res as { data?: { count?: number } }).data &&
      typeof (res as { data: { count?: number } }).data.count === "number"
    ) {
      return (res as { data: { count: number } }).data.count;
    }
    return -1;
  };

  const countsMatchExpected = (activeCount: number, archivedCount: number) => {
    if (op == null) return activeCount >= 0 && archivedCount >= 0;
    return (
      !isStaleProcessingCountVersusExpected(
        "active",
        activeCount,
        expected,
        op,
      ) &&
      !isStaleProcessingCountVersusExpected(
        "archived",
        archivedCount,
        expected,
        op,
      )
    );
  };

  const pinOptimisticCounts = () => {
    dispatch(
      processingJobsApi.util.updateQueryData(
        "countProcessingJobs",
        {},
        (draft) => {
          if (!draft) return;
          draft.count = expected.active;
        },
      ),
    );
    dispatch(
      processingJobsApi.util.updateQueryData(
        "countProcessingJobs",
        { archived: true },
        (draft) => {
          if (!draft) return;
          draft.count = expected.archived;
        },
      ),
    );
  };

  const reapplyOptimisticLists = () => {
    if (!op) return;
    if (op === "delete") {
      if (expectedJobIds.length) {
        patchProcessingJobsLists(dispatch, expectedJobIds, "delete");
      } else if (isEmptyArchivedDeleteTarget(expected)) {
        emptyProcessingJobsListCaches(dispatch, "archived");
      }
      return;
    }
    if (!expectedJobIds.length) return;
    patchProcessingJobsLists(dispatch, expectedJobIds, op);
  };

  const invalidateAuthoritativeLists = () => {
    dispatch(
      processingJobsApi.util.invalidateTags([
        { type: "ProcessingJobs", id: "LIST" },
      ]),
    );
  };

  /**
   * Last-chance authoritative check before warning the operator. If Active +
   * Archived counts already match the mutation outcome (and source membership
   * is clear), apply server LIST invalidate — do not leave a false
   * "best-known" toast when the backend is already correct.
   */
  const tryAcceptAuthoritativeState = async (): Promise<boolean> => {
    const results = await Promise.all([
      dispatch(
        processingJobsApi.endpoints.countProcessingJobs.initiate(
          {},
          { forceRefetch: true },
        ),
      ),
      dispatch(
        processingJobsApi.endpoints.countProcessingJobs.initiate(
          { archived: true },
          { forceRefetch: true },
        ),
      ),
    ]);
    if (generation !== processingReconcileGeneration) return false;
    const activeCount = readCount(results[0]);
    const archivedCount = readCount(results[1]);
    if (op != null && !countsMatchExpected(activeCount, archivedCount)) {
      return false;
    }

    if (op && expectedJobIds.length > 0 && op !== "delete") {
      const destArgs =
        op === "archive" ? DEFAULT_ARCHIVED_LIST_ARGS : DEFAULT_ACTIVE_LIST_ARGS;
      const sourceArgs =
        op === "archive" ? DEFAULT_ACTIVE_LIST_ARGS : DEFAULT_ARCHIVED_LIST_ARGS;
      const [destRes, sourceRes] = await Promise.all([
        dispatch(
          processingJobsApi.endpoints.listProcessingJobs.initiate(destArgs, {
            forceRefetch: true,
          }),
        ),
        dispatch(
          processingJobsApi.endpoints.listProcessingJobs.initiate(sourceArgs, {
            forceRefetch: true,
          }),
        ),
      ]);
      if (generation !== processingReconcileGeneration) return false;
      const destJobs =
        destRes && "data" in destRes && destRes.data ? destRes.data.jobs : null;
      const sourceJobs =
        sourceRes && "data" in sourceRes && sourceRes.data
          ? sourceRes.data.jobs
          : null;
      if (listContainsExpectedJobIds(destJobs, expectedJobIds)) {
        invalidateAuthoritativeLists();
        return true;
      }
      // Counts match + source partition no longer holds the moved ids →
      // destination page lag is not an operator-facing reconcile failure.
      if (sourceClearedForLifecycleMove(sourceJobs, expectedJobIds)) {
        reapplyOptimisticLists();
        invalidateAuthoritativeLists();
        return true;
      }
      return false;
    }

    invalidateAuthoritativeLists();
    return true;
  };

  const failReconcile = async () => {
    pinOptimisticCounts();
    reapplyOptimisticLists();
    if (op === "delete") {
      opts.onSettled?.("kept-optimistic");
      return;
    }
    // Mutation already succeeded — only warn when authoritative state still
    // cannot be determined after a final refetch.
    try {
      if (await tryAcceptAuthoritativeState()) {
        opts.onSettled?.("converged");
        return;
      }
    } catch {
      // fall through to operator warning
    }
    if (generation !== processingReconcileGeneration) return;
    pinOptimisticCounts();
    reapplyOptimisticLists();
    opts.onReconcileFailed?.();
    opts.onSettled?.("kept-optimistic");
  };

  const attemptReconcile = (attempt: number) => {
    window.setTimeout(() => {
      if (generation !== processingReconcileGeneration) return;

      void (async () => {
        if (generation !== processingReconcileGeneration) return;
        try {
          const results = await Promise.all([
            dispatch(
              processingJobsApi.endpoints.countProcessingJobs.initiate(
                {},
                { forceRefetch: true },
              ),
            ),
            dispatch(
              processingJobsApi.endpoints.countProcessingJobs.initiate(
                { archived: true },
                { forceRefetch: true },
              ),
            ),
          ]);
          if (generation !== processingReconcileGeneration) return;

          if (op != null) {
            const buckets: Array<"active" | "archived"> = ["active", "archived"];
            let stale = false;
            for (let i = 0; i < buckets.length; i += 1) {
              const count = readCount(results[i]);
              if (
                isStaleProcessingCountVersusExpected(
                  buckets[i],
                  count,
                  expected,
                  op,
                )
              ) {
                stale = true;
                break;
              }
            }
            if (stale && attempt + 1 < maxAttempts) {
              // Keep optimistic totals visible during laggy retries so a stale
              // Archived counter (e.g. 27 after wipe) does not flash on the tab.
              if (op === "delete" && isEmptyArchivedDeleteTarget(expected)) {
                pinOptimisticCounts();
                reapplyOptimisticLists();
              } else if (op !== "delete") {
                reapplyOptimisticLists();
              }
              attemptReconcile(attempt + 1);
              return;
            }
            if (stale) {
              await failReconcile();
              return;
            }
          }

          // Counts look caught up — verify destination list membership before
          // blind LIST invalidate (incomplete Archived page must not stick).
          if (op && expectedJobIds.length > 0 && op !== "delete") {
            const destArgs =
              op === "archive"
                ? DEFAULT_ARCHIVED_LIST_ARGS
                : DEFAULT_ACTIVE_LIST_ARGS;
            const sourceArgs =
              op === "archive"
                ? DEFAULT_ACTIVE_LIST_ARGS
                : DEFAULT_ARCHIVED_LIST_ARGS;
            const [listRes, sourceRes] = await Promise.all([
              dispatch(
                processingJobsApi.endpoints.listProcessingJobs.initiate(
                  destArgs,
                  { forceRefetch: true },
                ),
              ),
              dispatch(
                processingJobsApi.endpoints.listProcessingJobs.initiate(
                  sourceArgs,
                  { forceRefetch: true },
                ),
              ),
            ]);
            if (generation !== processingReconcileGeneration) return;
            const jobs =
              listRes && "data" in listRes && listRes.data
                ? listRes.data.jobs
                : null;
            const sourceJobs =
              sourceRes && "data" in sourceRes && sourceRes.data
                ? sourceRes.data.jobs
                : null;
            if (!listContainsExpectedJobIds(jobs, expectedJobIds)) {
              // Destination page incomplete — if source is already clear,
              // counts already matched, so accept authoritative invalidate.
              if (sourceClearedForLifecycleMove(sourceJobs, expectedJobIds)) {
                reapplyOptimisticLists();
                invalidateAuthoritativeLists();
                opts.onSettled?.("converged");
                return;
              }
              // Stale/incomplete list already wrote into the cache — restore
              // optimistic membership immediately, then retry.
              reapplyOptimisticLists();
              if (attempt + 1 < maxAttempts) {
                attemptReconcile(attempt + 1);
                return;
              }
              await failReconcile();
              return;
            }
          }

          // Delete: confirm removed ids are gone from both partitions.
          if (op === "delete" && expectedJobIds.length > 0) {
            const listResults = await Promise.all([
              dispatch(
                processingJobsApi.endpoints.listProcessingJobs.initiate(
                  DEFAULT_ACTIVE_LIST_ARGS,
                  { forceRefetch: true },
                ),
              ),
              dispatch(
                processingJobsApi.endpoints.listProcessingJobs.initiate(
                  DEFAULT_ARCHIVED_LIST_ARGS,
                  { forceRefetch: true },
                ),
              ),
            ]);
            if (generation !== processingReconcileGeneration) return;
            const stillPresent = listResults.some((listRes) => {
              const jobs =
                listRes && "data" in listRes && listRes.data
                  ? listRes.data.jobs
                  : null;
              return !listOmitsExpectedJobIds(jobs, expectedJobIds);
            });
            if (stillPresent) {
              reapplyOptimisticLists();
              if (attempt + 1 < maxAttempts) {
                attemptReconcile(attempt + 1);
                return;
              }
              pinOptimisticCounts();
              opts.onReconcileFailed?.();
              opts.onSettled?.("kept-optimistic");
              return;
            }
          }

          invalidateAuthoritativeLists();
          opts.onSettled?.("converged");
        } catch {
          if (
            generation === processingReconcileGeneration &&
            attempt + 1 < maxAttempts
          ) {
            reapplyOptimisticLists();
            attemptReconcile(attempt + 1);
          } else if (generation === processingReconcileGeneration) {
            // Transient fetch errors after a confirmed delete should not
            // alarm when we already hold the optimistic empty snapshot.
            if (op === "delete") {
              pinOptimisticCounts();
              opts.onSettled?.("kept-optimistic");
              return;
            }
            await failReconcile();
          }
        }
      })();
    }, attempt === 0 ? delayMs : PROCESSING_RECONCILE_RETRY_MS);
  };

  attemptReconcile(0);
}

export const {
  useListProcessingJobsQuery,
  useCountProcessingJobsQuery,
  useGetProcessingJobQuery,
  useListApprovedArtifactVersionsQuery,
  useGetApprovedArtifactVersionQuery,
  useCreateUploadMutation,
  useReplaceSongAudioMutation,
  useReprocessProcessingJobMutation,
  useBulkReprocessProcessingJobsMutation,
  useResumeProcessingJobMutation,
  useCorrectJobMetadataMutation,
  useArchiveProcessingJobMutation,
  useRestoreProcessingJobMutation,
  useBulkArchiveProcessingJobsMutation,
  useBulkRestoreProcessingJobsMutation,
  useCreateProcessingBulkOpMutation,
  useGetProcessingBulkOpQuery,
  useLazyGetProcessingBulkOpQuery,
  useCancelProcessingBulkOpMutation,
  useApplyProcessingBulkOpMutation,
  useDeleteProcessingJobMutation,
  useBulkDeleteProcessingJobsMutation,
  useRevokeApprovedArtifactVersionMutation,
} = processingJobsApi;

/** True when the music-processing API base URL has not been configured. */
export function isProcessingApiNotConfigured(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const anyErr = err as Record<string, unknown>;
  if (anyErr.error === "MUSIC_PROCESSING_API_NOT_CONFIGURED") return true;
  const nested = anyErr.error as Record<string, unknown> | undefined;
  if (nested && typeof nested === "object") {
    return nested.error === "MUSIC_PROCESSING_API_NOT_CONFIGURED";
  }
  return false;
}

/**
 * Extracts the machine-readable `details.reasonCode` that lifecycle endpoints
 * include on 400/404/409/500 rejections (U1-api). Returns "" when absent.
 */
export function extractProcessingErrorReasonCode(err: unknown): string {
  if (!err || typeof err !== "object") return "";
  const anyErr = err as Record<string, unknown>;
  const data = anyErr.data as Record<string, unknown> | undefined;
  const details = (data?.details ?? anyErr.details) as
    | Record<string, unknown>
    | undefined;
  if (details && typeof details.reasonCode === "string") {
    return details.reasonCode;
  }
  return "";
}

/** DRA-3 — 409 when another Replace Song Audio attempt is already in progress. */
export interface ReplacementAlreadyInProgressDetails {
  reasonCode: "replacement-already-in-progress";
  trackId: string | null;
  activeReplacementJobId: string | null;
  state: string | null;
  initiatedBy: string | null;
  initiatedAt: string | null;
  attempt: number | null;
  openReplacementPath: string | null;
  message: string;
}

export function extractReplacementAlreadyInProgress(
  err: unknown,
): ReplacementAlreadyInProgressDetails | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const data = (anyErr.data ?? anyErr) as Record<string, unknown>;
  const details = data?.details as Record<string, unknown> | undefined;
  if (!details || details.reasonCode !== "replacement-already-in-progress") {
    return null;
  }
  const jobId =
    typeof details.activeReplacementJobId === "string"
      ? details.activeReplacementJobId
      : null;
  const openPath =
    typeof details.openReplacementPath === "string"
      ? details.openReplacementPath
      : jobId
        ? `/processing-jobs/${encodeURIComponent(jobId)}`
        : null;
  const apiMessage =
    typeof data.message === "string" && data.message.trim()
      ? data.message.trim()
      : "A replacement for this Song is already in progress. Open the existing replacement attempt instead of starting another.";
  return {
    reasonCode: "replacement-already-in-progress",
    trackId: typeof details.trackId === "string" ? details.trackId : null,
    activeReplacementJobId: jobId,
    state: typeof details.state === "string" ? details.state : null,
    initiatedBy:
      typeof details.initiatedBy === "string" ? details.initiatedBy : null,
    initiatedAt:
      typeof details.initiatedAt === "string" ? details.initiatedAt : null,
    attempt: typeof details.attempt === "number" ? details.attempt : null,
    openReplacementPath: openPath,
    message: apiMessage,
  };
}

const DUPLICATE_UPLOAD_REASON_CODES = new Set<DuplicateUploadReasonCode>([
  "duplicate-upload",
  "duplicate-upload-in-flight",
  "duplicate-upload-checksum-existing-song",
  "duplicate-upload-checksum-orphan-job",
  "duplicate-active-song-identity",
  "duplicate-detection-unavailable",
  "duplicate-override-retired",
]);

const DUPLICATE_ACTIONS = new Set<DuplicateAction>([
  "open-existing-song-processing",
  "open-prior-song-processing",
  "open-existing-song",
  "replace-song-audio",
  "keep-separate-after-title-change",
  "cancel-upload",
]);

function parseDuplicateTrackMatch(raw: unknown): DuplicateTrackMatch | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.trackId !== "string" || !row.trackId) return null;
  return row as unknown as DuplicateTrackMatch;
}

function parseReplaceSongAudioAction(raw: unknown): ReplaceSongAudioAction | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (row.action !== "replace-song-audio") return null;
  return {
    action: "replace-song-audio",
    available: row.available === true,
    deferredTo: typeof row.deferredTo === "string" ? row.deferredTo : null,
    existingSong: parseDuplicateTrackMatch(row.existingSong),
    note: typeof row.note === "string" ? row.note : null,
  };
}

function inferDuplicateOutcome(
  reasonCode: DuplicateUploadReasonCode,
  details: Record<string, unknown>,
): DuplicateOutcome {
  const outcome = details.outcome;
  if (outcome === "BLOCK" || outcome === "STOP" || outcome === "REVIEW") {
    return outcome;
  }
  if (reasonCode === "duplicate-upload-in-flight") return "BLOCK";
  if (reasonCode === "duplicate-detection-unavailable") return "REVIEW";
  return "STOP";
}

/**
 * DRA-2 — parses a POST /uploads 409 duplicate rejection into a typed shape.
 * Returns null when the error is not a duplicate-upload rejection.
 */
export function extractDuplicateUploadDetails(
  err: unknown,
): DuplicateUploadDetails | null {
  if (!err || typeof err !== "object") return null;
  const anyErr = err as Record<string, unknown>;
  const data = (anyErr.data ?? anyErr) as Record<string, unknown>;
  const details = data?.details as Record<string, unknown> | undefined;
  if (!details || typeof details.reasonCode !== "string") return null;
  if (!DUPLICATE_UPLOAD_REASON_CODES.has(details.reasonCode as DuplicateUploadReasonCode)) {
    return null;
  }
  const reasonCode = details.reasonCode as DuplicateUploadReasonCode;
  const matches = (details.matches ?? {}) as Record<string, unknown>;
  const rawActions = Array.isArray(details.actions) ? details.actions : [];
  const actions = rawActions.filter(
    (value): value is DuplicateAction =>
      typeof value === "string" && DUPLICATE_ACTIONS.has(value as DuplicateAction),
  );
  const existingSong =
    parseDuplicateTrackMatch(details.existingSong) ??
    parseDuplicateTrackMatch(
      (details.replaceSongAudio as Record<string, unknown> | undefined)?.existingSong,
    );
  const apiMessage =
    typeof data.message === "string"
      ? data.message
      : typeof details.message === "string"
        ? details.message
        : null;

  return {
    reasonCode,
    outcome: inferDuplicateOutcome(reasonCode, details),
    signal: typeof details.signal === "string" ? details.signal : null,
    actions,
    canOverride: false,
    matches: {
      checksum: Array.isArray(matches.checksum)
        ? (matches.checksum as DuplicateUploadDetails["matches"]["checksum"])
        : [],
      checksumInFlight: Array.isArray(matches.checksumInFlight)
        ? (matches.checksumInFlight as DuplicateUploadDetails["matches"]["checksumInFlight"])
        : [],
      tracks: Array.isArray(matches.tracks)
        ? (matches.tracks as DuplicateUploadDetails["matches"]["tracks"])
        : [],
    },
    existingSong,
    replaceSongAudio: parseReplaceSongAudioAction(details.replaceSongAudio),
    warning:
      details.warning && typeof details.warning === "object"
        ? (details.warning as DuplicateUploadDetails["warning"])
        : null,
    message: apiMessage,
  };
}

/** Best-effort extraction of a human-readable error message. */
export function extractProcessingErrorMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (data && typeof data.message === "string" && data.message.length > 0) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}
